import type { FC, ReactNode } from "react";

type IdentityFlow = "checkin" | "checkout" | "request" | "reservation";

interface IdentityChoiceStepProps {
  onSoldier: () => void;
  onCivilian: () => void;
  onBack?: () => void;
  flow?: IdentityFlow;
}

const TITLES: Record<IdentityFlow, string> = {
  checkin: "Check In",
  checkout: "Check Out",
  request: "Report an Issue",
  reservation: "Find Your Stay",
};

const ShieldIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className="size-[22px]"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2l8 3v6c0 5-3.5 8-8 11-4.5-3-8-6-8-11V5z" />
  </svg>
);

const GuestIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className="size-[22px]"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.5-6 8-6s8 2 8 6" />
  </svg>
);

const ChevronIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className="size-[18px]"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

interface ChoiceButtonProps {
  icon: ReactNode;
  label: string;
  description: string;
  onClick: () => void;
}

const ChoiceButton: FC<ChoiceButtonProps> = ({
  icon,
  label,
  description,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-4 rounded-xl border border-white/10 bg-gradient-to-b from-white/[0.055] to-white/[0.02] p-5 text-left transition-all hover:border-accentGreen/40 hover:from-white/[0.08] hover:to-white/[0.03] active:scale-[0.98]"
  >
    <span className="flex w-9 shrink-0 justify-center text-white/70">
      {icon}
    </span>
    <span className="min-w-0">
      <span className="block font-oswald text-xl font-medium uppercase tracking-wider text-white">
        {label}
      </span>
      <span className="mt-1 block text-xs text-white/45">{description}</span>
    </span>
    <span className="ml-auto shrink-0 text-white/30">
      <ChevronIcon />
    </span>
  </button>
);

const IdentityChoiceStep: FC<IdentityChoiceStepProps> = ({
  onSoldier,
  onCivilian,
  onBack,
  flow = "checkin",
}) => (
  <div className="relative z-10 flex flex-1 flex-col items-center px-6">
    <div className="grid w-full max-w-sm flex-1 grid-rows-[1fr_auto_1fr]">
      <div className="flex flex-col justify-end pb-14">
        <div className="bc-fade-up text-center">
          <img
            src={new URL("../assets/ths-footer-logo.svg", import.meta.url).href}
            alt="Team Housing Solutions"
            className="mx-auto w-48 sm:w-56"
          />
        </div>
        <div className="bc-fade-up bc-fade-up-1 mt-10 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="bc-fade-up bc-fade-up-1 mt-10 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/25">
            Basecamp Lodging
          </p>
          <h1 className="mt-2 font-oswald text-4xl font-medium uppercase tracking-wide text-white sm:text-5xl">
            {TITLES[flow]}
          </h1>
        </div>
      </div>

      <div className="bc-fade-up bc-fade-up-2 space-y-3">
        <ChoiceButton
          icon={<ShieldIcon />}
          label="I have a DoD ID"
          description="Service members & DoD staff"
          onClick={onSoldier}
        />
        <ChoiceButton
          icon={<GuestIcon />}
          label="I'm a guest"
          description="Family, contractor or visitor"
          onClick={onCivilian}
        />
        <p className="pt-2 text-center text-[11px] text-white/30">
          Need help? Find a staff member
        </p>
      </div>

      <div className="bc-fade-up bc-fade-up-3 pt-5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="w-full py-3 text-sm font-medium text-white/25 transition-colors hover:text-white/40"
          >
            Go Back
          </button>
        )}
      </div>
    </div>
  </div>
);

export default IdentityChoiceStep;
